import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import FeedbackDashboard from "./FeedbackDashboard";
import PolicyUpdateNotification from "./PolicyUpdateNotification";
import DatabaseDashboard from "./DatabaseDashboard";
import CollectionDetail from "./CollectionDetail";
import SubscriptionDashboard from "./SubscriptionDashboard";
import LocalSourcesDashboard from "./LocalSourcesDashboard";
import VideoNewsDashboard from "./VideoNewsDashboard";
import BackendStatusBanner from "../components/BackendStatusBanner";
import {
  LayoutDashboard,
  MessageSquare,
  Mail,
  Database,
  CreditCard,
  Newspaper,
  Youtube,
  LogOut,
  Menu,
  X,
} from "lucide-react";

const AdminPanel = () => {
  const { adminToken, adminUser, logoutAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("feedback");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Set active section based on route
  useEffect(() => {
    if (location.pathname === "/admin/feedback") {
      setActiveSection("feedback");
    } else if (location.pathname === "/admin/policy-updates") {
      setActiveSection("policy-updates");
    } else if (location.pathname.startsWith("/admin/databases")) {
      setActiveSection("databases");
    } else if (location.pathname === "/admin/subscriptions") {
      setActiveSection("subscriptions");
    } else if (location.pathname === "/admin/local-sources") {
      setActiveSection("local-sources");
    } else if (location.pathname === "/admin/video-news") {
      setActiveSection("video-news");
    }
  }, [location.pathname]);

  const isAdmin = adminUser?.isAdmin === true && adminUser?.username === "admin";

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-50 dark:from-gray-900 dark:via-red-900 dark:to-gray-900">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-red-500 border-t-transparent" />
      </div>
    );
  }

  if (!adminToken || !adminUser || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-red-50 via-white to-red-50 dark:from-gray-900 dark:via-red-900 dark:to-gray-900">
        <div className="max-w-md w-full">
          <div className="backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 border border-white/20 dark:border-gray-700/30 rounded-2xl p-6 shadow-2xl shadow-black/10 dark:shadow-black/30">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
              Admin Access Required
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Please login with admin credentials to access the admin panel.
            </p>
            <button
              onClick={() => navigate("/admin/login")}
              className="inline-block px-6 py-2.5 backdrop-blur-sm bg-red-500/80 dark:bg-red-600/80 text-white rounded-xl hover:bg-red-600 dark:hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40"
            >
              Go to Admin Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logoutAdmin();
    navigate("/admin/login");
  };

  const menuItems = [
    {
      id: "feedback",
      label: "Feedback",
      icon: MessageSquare,
      component: <FeedbackDashboard />,
    },
    {
      id: "policy-updates",
      label: "Policy Updates",
      icon: Mail,
      component: <PolicyUpdateNotification />,
    },
    {
      id: "databases",
      label: "Databases",
      icon: Database,
      component: <DatabaseDashboard />,
    },
    {
      id: "subscriptions",
      label: "Subscriptions",
      icon: CreditCard,
      component: <SubscriptionDashboard />,
    },
    {
      id: "local-sources",
      label: "Local Sources",
      icon: Newspaper,
      component: <LocalSourcesDashboard />,
    },
    {
      id: "video-news",
      label: "Video News",
      icon: Youtube,
      component: <VideoNewsDashboard />,
    },
    // Add more sections here in the future
    // {
    //   id: "users",
    //   label: "Users",
    //   icon: Users,
    //   component: <UsersDashboard />,
    // },
  ];

  const activeMenuItem = menuItems.find((item) => item.id === activeSection);
  const collectionMatch = location.pathname.match(/^\/admin\/databases\/([^/]+)$/);
  const collectionName = collectionMatch ? decodeURIComponent(collectionMatch[1]) : null;

  const renderActiveComponent = () => {
    if (activeSection === "databases" && collectionName) {
      return <CollectionDetail key={collectionName} collectionName={collectionName} />;
    }
    return activeMenuItem?.component;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 dark:from-gray-900 dark:via-red-900 dark:to-gray-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-red-300 dark:bg-red-900 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-red-200 dark:bg-red-800 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-red-300 dark:bg-red-900 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 border-b border-white/20 dark:border-gray-700/30 px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-lg shadow-black/5">
        <h1 className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 dark:from-red-400 dark:to-red-500 bg-clip-text text-transparent">
          Admin Panel
        </h1>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-xl backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 hover:bg-white/70 dark:hover:bg-gray-700/70 border border-white/20 dark:border-gray-600/30 transition-all duration-300 shadow-lg shadow-black/5"
        >
          {sidebarOpen ? (
            <X className="w-6 h-6 text-gray-700 dark:text-gray-300" />
          ) : (
            <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
          )}
        </button>
      </div>

      <div className="flex relative z-10">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } fixed lg:static inset-y-0 left-0 z-30 w-64 backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 border-r border-white/20 dark:border-gray-700/30 transition-transform duration-300 ease-in-out lg:translate-x-0 shadow-2xl shadow-black/10 dark:shadow-black/30`}
        >
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="p-6 border-b border-white/20 dark:border-gray-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2.5 backdrop-blur-sm bg-gradient-to-br from-red-500/80 to-red-600/80 dark:from-red-600/80 dark:to-red-700/80 rounded-xl shadow-lg shadow-red-500/30">
                  <LayoutDashboard className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-bold bg-gradient-to-r from-red-600 to-red-700 dark:from-red-400 dark:to-red-500 bg-clip-text text-transparent truncate">
                    Admin Panel
                  </h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium truncate">
                    {adminUser?.username || "Admin"}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setSidebarOpen(false); // Close sidebar on mobile after selection
                      // Update URL based on section
                      if (item.id === "feedback") {
                        navigate("/admin/feedback");
                      } else if (item.id === "policy-updates") {
                        navigate("/admin/policy-updates");
                      } else if (item.id === "databases") {
                        navigate("/admin/databases");
                      } else if (item.id === "subscriptions") {
                        navigate("/admin/subscriptions");
                      } else if (item.id === "local-sources") {
                        navigate("/admin/local-sources");
                      } else if (item.id === "video-news") {
                        navigate("/admin/video-news");
                      } else {
                        navigate("/admin/panel");
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      isActive
                        ? "backdrop-blur-sm bg-gradient-to-r from-red-500/80 to-red-600/80 dark:from-red-600/80 dark:to-red-700/80 text-white shadow-lg shadow-red-500/30 transform scale-[1.02]"
                        : "text-gray-700 dark:text-gray-300 hover:backdrop-blur-sm hover:bg-white/50 dark:hover:bg-gray-700/50 border border-transparent hover:border-white/20 dark:hover:border-gray-600/30"
                    }`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-white" : ""}`} />
                    <span className={`font-medium truncate ${isActive ? "text-white" : ""}`}>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Logout Button */}
            <div className="p-4 border-t border-white/20 dark:border-gray-700/30">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl backdrop-blur-sm bg-red-500/80 dark:bg-red-600/80 text-white hover:bg-red-600 dark:hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 hover:scale-[1.02]"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 backdrop-blur-sm bg-black/30 dark:bg-black/50 z-20 lg:hidden transition-opacity duration-300"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen p-3 sm:p-4 md:p-6 lg:p-8 overflow-x-hidden">
          <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-3xl border border-white/20 dark:border-gray-700/30 shadow-2xl shadow-black/10 dark:shadow-black/30 min-h-full p-4 sm:p-6 lg:p-8 overflow-x-hidden">
            <BackendStatusBanner />
            {renderActiveComponent() || (
              <div className="p-8">
                <p className="text-gray-600 dark:text-gray-300">
                  Select a section from the sidebar
                </p>
              </div>
            )}
          </div>
        </main>
      </div>

      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;


